import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  ArrowLeft,
  Save,
  Eye,
  FileText,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

interface KBArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string | null;
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  tags: string[] | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  isFeatured: boolean;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface KBCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

interface FormData {
  title: string;
  content: string;
  excerpt: string;
  categoryId: string;
  tags: string[];
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
}

const initialFormData: FormData = {
  title: '',
  content: '',
  excerpt: '',
  categoryId: '',
  tags: [],
  status: 'DRAFT',
};

export default function KBArticleFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!id;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [tagInput, setTagInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Fetch existing article if editing
  const { data: article, isLoading: articleLoading } = useQuery({
    queryKey: ['kbArticle', id],
    queryFn: async () => {
      const response = await api.get(`/knowledge-base/articles/${id}`);
      return response.data.data as KBArticle;
    },
    enabled: isEditing,
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['kbCategories'],
    queryFn: async () => {
      const response = await api.get('/knowledge-base/categories');
      return response.data.data as KBCategory[];
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (article) {
      setFormData({
        title: article.title,
        content: article.content,
        excerpt: article.excerpt || '',
        categoryId: article.category?.id || '',
        tags: article.tags || [],
        status: article.status,
      });
    }
  }, [article]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      return api.post('/knowledge-base/articles', data);
    },
    onSuccess: (response) => {
      toast({ title: 'Success', description: 'Article created successfully' });
      navigate(`/knowledge-base/${response.data.data.id}`);
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create article', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: FormData) => {
      return api.put(`/knowledge-base/articles/${id}`, data);
    },
    onSuccess: () => {
      toast({ title: 'Success', description: 'Article updated successfully' });
      navigate(`/knowledge-base/${id}`);
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update article', variant: 'destructive' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({ 
      ...formData, 
      tags: formData.tags.filter(tag => tag !== tagToRemove) 
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const categories: KBCategory[] = categoriesData || [];
  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEditing && articleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/knowledge-base')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditing ? 'Edit Article' : 'Create Article'}
            </h1>
            <p className="text-muted-foreground">
              {isEditing ? 'Update article details' : 'Create a new knowledge base article'}
            </p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? (
            <>
              <FileText className="w-4 h-4 mr-2" />
              Edit
            </>
          ) : (
            <>
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </>
          )}
        </Button>
      </div>

      {showPreview ? (
        /* Preview Mode */
        <Card>
          <CardHeader>
            <CardTitle>{formData.title || 'Untitled Article'}</CardTitle>
          </CardHeader>
          <CardContent>
            {formData.excerpt && (
              <p className="text-muted-foreground mb-4 italic">{formData.excerpt}</p>
            )}
            <div 
              className="prose prose-sm max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: formData.content || '<p>No content yet...</p>' }}
            />
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t">
                {formData.tags.map((tag, idx) => (
                  <span 
                    key={idx} 
                    className="px-3 py-1 text-sm bg-muted rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Edit Mode */
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Article Content</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Enter article title"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="excerpt">Excerpt</Label>
                    <Input
                      id="excerpt"
                      value={formData.excerpt}
                      onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      placeholder="Brief summary of the article"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      A short description shown in article lists
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="content">Content (Markdown/HTML) *</Label>
                    <textarea
                      id="content"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Write your article content here... Supports HTML and Markdown."
                      className="w-full min-h-[400px] px-3 py-2 rounded-md border border-input bg-background text-sm font-mono resize-y"
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Supports HTML tags and basic Markdown formatting
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="status">Status *</Label>
                    <select
                      id="status"
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as FormData['status'] })}
                      required
                    >
                      <option value="DRAFT">Draft</option>
                      <option value="PUBLISHED">Published</option>
                      <option value="ARCHIVED">Archived</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <select
                      id="category"
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      value={formData.categoryId}
                      onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                      required
                    >
                      <option value="">Select a category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Tags</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Add a tag"
                    />
                    <Button 
                      type="button" 
                      variant="secondary"
                      onClick={handleAddTag}
                    >
                      Add
                    </Button>
                  </div>
                  
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map((tag, idx) => (
                        <span 
                          key={idx} 
                          className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-muted rounded-full"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="hover:text-destructive"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-xs text-muted-foreground">
                    Press Enter to add tags
                  </p>
                </CardContent>
              </Card>

              {/* Actions */}
              <Card>
                <CardContent className="p-4 space-y-2">
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isPending}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isPending ? 'Saving...' : (isEditing ? 'Update Article' : 'Create Article')}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate('/knowledge-base')}
                  >
                    Cancel
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
